import type {
  AcpSessionChangeData,
  AcpSessionConfigOption,
  CrowdexUIMessage,
} from '@crowdex/agent-contracts';

export function hasConversationActivity(messages: readonly CrowdexUIMessage[]): boolean {
  return messages.some((message) =>
    message.parts.some((part) => part.type !== 'data-sessionEvent'),
  );
}

/** Latest explicitly recorded agent target, used when restoring across runtime aliases. */
export function latestSessionAgentName(messages: readonly CrowdexUIMessage[]): string | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message === undefined) continue;
    for (let partIndex = message.parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = message.parts[partIndex];
      if (part?.type !== 'data-sessionEvent') continue;
      for (let changeIndex = part.data.changes.length - 1; changeIndex >= 0; changeIndex -= 1) {
        const change = part.data.changes[changeIndex];
        if (change?.field === 'agent' && change.to.trim().length > 0) return change.to;
      }
    }
  }
  return null;
}

/**
 * Returns configuration reported by the current agent session only.
 *
 * Agent handoffs deliberately preserve the transcript. The configuration metadata on older
 * assistant messages belongs to the previous ACP process, so crossing an agent session event
 * would otherwise leak options such as Codex sandbox modes into Claude Code's selector.
 */
export function latestSessionConfigOptions(
  messages: readonly CrowdexUIMessage[],
): readonly AcpSessionConfigOption[] | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message === undefined) continue;
    const startsAnotherAgentSession = message.parts.some(
      (part) =>
        part.type === 'data-sessionEvent' &&
        part.data.changes.some((change) => change.field === 'agent'),
    );
    if (startsAnotherAgentSession) return null;
    if ((message.metadata?.sessionConfigOptions?.length ?? 0) > 0) {
      return message.metadata?.sessionConfigOptions ?? null;
    }
  }
  return null;
}

/**
 * The model the agent's latest reply says it ran (its metadata's `modelId`), within the current
 * agent session: what names the default once the agent has answered, when nothing said it before.
 */
export function latestReplyModelId(messages: readonly CrowdexUIMessage[]): string | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message === undefined) continue;
    if (
      message.parts.some(
        (part) =>
          part.type === 'data-sessionEvent' &&
          part.data.changes.some((change) => change.field === 'agent'),
      )
    )
      return null;
    const modelId = message.role === 'assistant' ? message.metadata?.modelId : undefined;
    if (typeof modelId === 'string' && modelId.trim().length > 0) return modelId.trim();
  }
  return null;
}

export function withSessionEvent(
  messages: readonly CrowdexUIMessage[],
  changes: readonly AcpSessionChangeData[],
  metadata?: CrowdexUIMessage['metadata'],
): CrowdexUIMessage[] {
  const meaningfulChanges = changes.filter(
    (change) => change.to.length > 0 && change.from !== change.to,
  );
  if (!hasConversationActivity(messages) || meaningfulChanges.length === 0) {
    return [...messages];
  }
  return [
    ...messages,
    {
      id: `session-event-${crypto.randomUUID()}`,
      role: 'assistant',
      ...(metadata === undefined ? {} : { metadata }),
      parts: [
        {
          type: 'data-sessionEvent',
          data: { changes: meaningfulChanges },
        },
      ],
    },
  ];
}
